import { Dispatch, SetStateAction, useMemo } from 'react';
import Modal from '../../components/modal/Modal';
import { Col, isZeroOrDefined, Row } from '../../lib/utils';
import { CoachReportWriteRateList } from '../../services/useFeedDashboardQueryService';
import FeedCenterStatisticsFeedWritingPercentIcon from './Icon/FeedCenterStatisticsFeedWritingPercentIcon';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../components/Table/Table';
import dayjs from 'dayjs';
import SvgIcon from '../../components/SvgIcon';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    coachReportWriteRateList: CoachReportWriteRateList[];
    month?: number;
    coachId?: string; // Optional coachId for navigation
};
const columnHelper = createColumnHelper<CoachReportWriteRateList>();

// 테이블 컬럼 정의

const ModalFeedCoachStatisticsPageMemberWritingRate = ({
    setIsVisible,
    coachReportWriteRateList,
    month,
    coachId,
}: Props) => {
    const navigate = useNavigate();
    const columns = useMemo(
        () => [
            columnHelper.accessor('memberName', {
                id: 'memberName',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={21} color={'#5D6166'} letterSpacing={-0.5}>
                        회원
                    </DemiLightText>
                ),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                        {info.getValue()}
                    </RegularText>
                ),
                size: 133,
                enableSorting: false,
            }),
            columnHelper.accessor('reportWriteRate', {
                id: 'reportWriteRate',
                header: ({ column }) => (
                    <Row className="flex w-full items-center  " onClick={column.getToggleSortingHandler()}>
                        <RegularText fontSize={16} lineHeight={21} color="#5D6166" className="mr-[14px]">
                            작성률
                        </RegularText>
                        {column.getIsSorted() === 'asc' && <SvgIcon name="SvgSortArrow" fill={'#427BE8'} size={14} />}
                        {column.getIsSorted() === 'desc' && (
                            <SvgIcon name="SvgSortArrow" fill={'#427BE8'} size={14} className="rotate-180" />
                        )}
                        {!column.getIsSorted() && (
                            <Col className="gap-[1px] ml-[4.8px]">
                                <SvgIcon name="SvgFillArrow" size={9} fill="#C6C8C9" />
                                <SvgIcon name="SvgFillArrow" size={9} fill="#C6C8C9" className="rotate-180" />
                            </Col>
                        )}
                    </Row>
                ),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                        {isZeroOrDefined(info.getValue()) ? info.getValue() + '%' : '-'}
                    </RegularText>
                ),
                size: 171,
                enableSorting: true,
                meta: {
                    isSortIconVisible: false,
                },
            }),

            columnHelper.accessor('reportCount', {
                id: 'reportCount',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={21} color={'#5D6166'} letterSpacing={-0.5}>
                        작성수/수업 수
                    </DemiLightText>
                ),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>{`${
                        isZeroOrDefined(info.getValue()) ? info.getValue() : '-'
                    }/${
                        isZeroOrDefined(info.row.original.totalScheduleCount)
                            ? info.row.original.totalScheduleCount
                            : '-'
                    }`}</RegularText>
                ),
                size: 132,
                enableSorting: false,
            }),
            columnHelper.accessor('latestScheduleDate', {
                id: 'latestScheduleDate',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={21} color={'#5D6166'} letterSpacing={-0.5}>
                        최근 수업일
                    </DemiLightText>
                ),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                        {isEmpty(info.getValue()) ? '-' : dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm')}
                    </RegularText>
                ),
                size: 252,
                enableSorting: false,
            }),
            columnHelper.accessor('latestReportDate', {
                id: 'latestReportDate',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={21} color={'#5D6166'} letterSpacing={-0.5}>
                        최근 작성일
                    </DemiLightText>
                ),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                        {isEmpty(info.getValue()) ? '-' : dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm')}
                    </RegularText>
                ),
                size: 252,
                enableSorting: false,
            }),
            columnHelper.accessor('postId', {
                id: 'postId',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={21} color={'#5D6166'} letterSpacing={-0.5}>
                        일지 피드
                    </DemiLightText>
                ),
                cell: ({ row }) => (
                    <div className="group w-fit cursor-pointer" onClick={() => onClickMember(row.original)}>
                        <SvgIcon
                            name="SvgCenterStatisticsFeedWritingPercent"
                            size={20}
                            fill="#ABB1B5"
                            className="group-hover:fill-primary" // colors.primary 값
                        />
                    </div>
                ),
                size: 121,
                enableSorting: false,
            }),
        ],
        [columnHelper]
    );

    const onClickMember = (row: any) => {
        const memberId = row.memberId;
        navigate('/feed/member', {
            state: {
                coachId: coachId,
                memberId: memberId,
            },
        });
    };
    return (
        <Modal setIsVisible={setIsVisible} whiteViewWidth={1174} borderRadius={25} isCloseButton>
            <Col className="flex w-full  py-[22px] px-[35px] h-[588px]">
                <Row className="flex items-center">
                    <FeedCenterStatisticsFeedWritingPercentIcon width={52} height={52} iconSize={26} />
                    <MediumText fontSize={30} lineHeight={35} color={colors.Text_1}>
                        회원별 {month}월 일지 작성률 현황
                    </MediumText>
                </Row>
                <Col className="flex w-full h-full mt-[30px]">
                    {coachReportWriteRateList && (
                        <Table
                            columns={columns}
                            data={coachReportWriteRateList}
                            headerColor="#F4F5F7"
                            isIndex
                            rowRadius={0}
                            rowHeight={71}
                            className="mb-[30px]"
                            isRowBorder
                            indexFontSize={16}
                            indexColumnWidth={43}
                        />
                    )}
                </Col>
            </Col>
        </Modal>
    );
};

export default ModalFeedCoachStatisticsPageMemberWritingRate;
